import {
  ComponentProps,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import isEqual from 'react-fast-compare';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { OkButton } from 'src/components/Buttons/OkButton';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { isSuccess } from 'src/utils/errorUtils';
import { posChangedField } from 'src/utils/posFieldUtils';
import {
  getMarketplaceSaleInputFromListing,
  getMarketplaceSaleInputFromSale,
  getSaleDetailsRelativeUrl,
} from 'src/utils/saleUtils';
import { EntityWithRealTickets } from 'src/utils/ticketUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  ApiException,
  Event,
  Feature,
  ListingDetails,
  MarketplaceSaleInput,
  SaleClient,
  SaleDetails,
} from 'src/WebApiController';

import { CancellableFormFooter } from '../common';
import { CancellableFormHeader } from '../common/CancellableFormHeader';
import { ConnectedEventEntityHeader } from '../common/EventEntityHeader';
import { modalDetails } from '../common/Modals.css';
import { Summary } from '../common/Summary';
import { ModalProps } from '../Modal';
import {
  ModalBody,
  ModalBodyHeaderContainer,
  ModalFooter,
} from '../Modal/Modal.styled';
import { MarketplaceSaleBody } from './MarketplaceSaleBody';

type MarketplaceSaleProps = {
  cancelTo?: ModalProps;
};

export const MarketplaceSale = ({ cancelTo }: MarketplaceSaleProps) => {
  const { posEntity, event } =
    useActivePosEntityContext<EntityWithRealTickets>();
  const listing =
    posEntity?.entityType === ActionOutboxEntityType.Listing
      ? (posEntity as ListingDetails)
      : undefined;
  const sale =
    posEntity?.entityType === ActionOutboxEntityType.Sale
      ? (posEntity as SaleDetails)
      : undefined;

  const hasMarketplaceSaleAvoidIdPrefillFeature = useUserHasFeature(
    Feature.MarketplaceSaleAvoidIdPrefill
  );
  const ihdTimezoneFix = useUserHasFeature(Feature.IhdTimezoneFix);

  const methods = useForm<MarketplaceSaleInput>({
    defaultValues:
      listing?.tickets?.length && event
        ? getMarketplaceSaleInputFromListing(
            listing,
            event,
            hasMarketplaceSaleAvoidIdPrefillFeature,
            ihdTimezoneFix
          )
        : sale && event
          ? getMarketplaceSaleInputFromSale(sale, event, ihdTimezoneFix)
          : undefined,
  });

  useEffect(() => {
    if (methods.formState.defaultValues === undefined && event) {
      if (listing?.tickets?.length) {
        methods.reset(
          getMarketplaceSaleInputFromListing(
            listing,
            event,
            hasMarketplaceSaleAvoidIdPrefillFeature,
            ihdTimezoneFix
          )
        );
      } else if (sale) {
        methods.reset(
          getMarketplaceSaleInputFromSale(sale, event, ihdTimezoneFix)
        );
      }
    }
  }, [
    methods,
    event,
    listing,
    hasMarketplaceSaleAvoidIdPrefillFeature,
    ihdTimezoneFix,
    sale,
  ]);

  return (
    <FormProvider {...methods}>
      <SaleContent
        {...methods}
        cancelTo={cancelTo}
        listing={listing}
        sale={sale}
        event={event}
      />
    </FormProvider>
  );
};

export const SaleContent = ({
  listing,
  sale,
  event,
  cancelTo,
  formState,
  handleSubmit,
  watch,
  getValues,
  setError,
  setValue,
}: Omit<
  ComponentProps<typeof FormProvider<MarketplaceSaleInput, unknown>>,
  'children'
> &
  MarketplaceSaleProps & {
    listing?: ListingDetails;
    sale?: SaleDetails;
    event?: Event | null;
  }) => {
  const { showErrorDialog, genericError } = useClientLogContext();
  const { activeAccountWebClientConfig } = useAppContext();
  const { setModal, closeModal } = useContext(ModalContext);
  const { setActivePosEntity, isLoading: entityIsLoading } =
    useActivePosEntityContext();

  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const onSubmit = useCallback(
    async (saleFormValue: MarketplaceSaleInput) => {
      setIsLoading(true);
      tryInvokeApi(
        async () => {
          const client = new SaleClient(activeAccountWebClientConfig);

          const listingId = saleFormValue.listingId ?? sale?.listingId ?? 0;

          const r = await client.mergeMarketplaceSaleForListingId(listingId, {
            ...saleFormValue,
            listingId,
          });

          if (isSuccess(r)) {
            if (sale) {
              await setActivePosEntity(sale.id, sale.idOnMkp, true);
            } else if (r.entityId) {
              navigate(getSaleDetailsRelativeUrl(r.entityId));
            }
            if (cancelTo) {
              setModal(cancelTo);
            } else {
              closeModal(true);
            }
          } else {
            showErrorDialog(
              'SaleClient.mergeMarketplaceSale',
              {
                message: r.message ?? genericError,
                status: r.status!,
              } as ApiException,
              {
                trackErrorData: saleFormValue,
              }
            );
          }
        },
        (error) => {
          showErrorDialog('SaleClient.mergeMarketplaceSale', error, {
            trackErrorData: saleFormValue,
          });
        },
        () => {
          setIsLoading(false);
        }
      );
    },
    [
      activeAccountWebClientConfig,
      cancelTo,
      closeModal,
      genericError,
      navigate,
      sale,
      setActivePosEntity,
      setModal,
      showErrorDialog,
    ]
  );

  const requiredMsg = useContent(ContentId.Required);
  const inhandDateMustBeforeEventDate = useContent(
    ContentId.InHandDateMustBeBeforeEventDate
  );

  const onSubmitHandler = useCallback(() => {
    const formValues = getValues();
    if (isEqual(formValues, formState.defaultValues)) return;

    if (!formValues.totalNetProceeds?.value) {
      formValues.totalNetProceeds = posChangedField(0);
    }

    if (sale?.listingId != null && !formValues.listingId) {
      setValue('listingId', sale.listingId);
    }

    if (!sale) {
      if (!formValues.saleDate) {
        setError('saleDate', { message: requiredMsg }, { shouldFocus: true });
      }

      if (!formValues.inHandDate?.value) {
        setError('inHandDate', { message: requiredMsg }, { shouldFocus: true });
      } else if (
        new Date(formValues.inHandDate.value) >= new Date(event!.dates.start!)
      ) {
        setError(
          'inHandDate',
          { message: inhandDateMustBeforeEventDate },
          { shouldFocus: true }
        );
      }
      if ((formValues.quantitySold?.value ?? 0) <= 0) {
        setError(
          'quantitySold',
          { message: requiredMsg },
          { shouldFocus: true }
        );
      }

      if (listing?.tickets?.find((t) => !!t.seat)) {
        // If the listing tickets has seats - so better the sale
        if (!formValues.seatFrom?.value || !formValues.seatTo?.value) {
          setError('seatFrom', { message: requiredMsg }, { shouldFocus: true });
        }
      }
    }

    handleSubmit(onSubmit)();
  }, [
    getValues,
    formState.defaultValues,
    event,
    listing?.tickets,
    handleSubmit,
    onSubmit,
    setError,
    setValue,
    sale,
    requiredMsg,
    inhandDateMustBeforeEventDate,
  ]);

  const formValues = watch();

  const formHasChanges = useMemo(() => {
    return !isEqual(formValues, formState.defaultValues);
  }, [formState.defaultValues, formValues]);

  return (
    <>
      <CancellableFormHeader
        cancelTo={cancelTo}
        disabled={isLoading || formState.isSubmitting}
        showDialogOnCancel={formHasChanges}
      >
        <ConnectedEventEntityHeader
          title={
            <>
              <Content
                id={
                  sale
                    ? ContentId.EditMarketplaceSale
                    : ContentId.AddMarketplaceSale
                }
              />
            </>
          }
        />
      </CancellableFormHeader>

      <ModalBody>
        <div className={modalDetails}>
          {event && (listing || sale) && (
            <ModalBodyHeaderContainer>
              <Summary event={event} posEntity={sale || listing} />
            </ModalBodyHeaderContainer>
          )}
          {entityIsLoading ? (
            <PosSpinner />
          ) : (
            <MarketplaceSaleBody
              disabled={isLoading}
              listing={listing}
              sale={sale}
            />
          )}
        </div>
      </ModalBody>

      <ModalFooter>
        <CancellableFormFooter
          cancelTo={cancelTo}
          disabled={isLoading || entityIsLoading}
          showDialogOnCancel={formHasChanges}
        >
          <OkButton
            onClick={onSubmitHandler}
            disabled={isLoading || !formHasChanges}
            textContentId={ContentId.Save}
            data-testid="marketplace-sale-save-button"
          />
        </CancellableFormFooter>
      </ModalFooter>
    </>
  );
};
